import { useState, type FormEvent, type ReactNode } from 'react';
import { KeyRound } from 'lucide-react';
import { Button, Dialog, Field, Input } from '@/components/ui';
import { useRevokeAgent } from '@/hooks/useNodes';
import { toast } from '@/store/toastStore';
import type { NodeView } from '@/types/api';
import { errorText } from '@/utils/errors';

/**
 * Revoking an agent's key (ADR-0009): for a key that may have leaked, or a
 * server being handed to someone else. The sites keep serving; only the panel
 * stops listening to that key.
 */
export function RevokeAgentDialog({
  node,
  open,
  onClose,
}: {
  readonly node: NodeView;
  readonly open: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const [typed, setTyped] = useState('');

  const revoke = useRevokeAgent({
    onSuccess: () => {
      toast.success('Ключ агента отозван');
      close();
    },
  });

  function close(): void {
    if (revoke.isPending) return;
    setTyped('');
    revoke.reset();
    onClose();
  }

  const matches = typed.trim().toLowerCase() === node.name;

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (matches) revoke.mutate({ id: node.id, confirmation: typed.trim() });
  }

  return (
    <Dialog
      open={open}
      title="Отозвать ключ агента"
      description={node.name}
      onClose={close}
      size="sm"
      footer={
        <>
          <Button onClick={close} disabled={revoke.isPending}>
            Отмена
          </Button>
          <Button variant="danger-solid" icon={KeyRound} busy={revoke.isPending} disabled={!matches} type="submit" form="revoke-agent">
            Отозвать
          </Button>
        </>
      }
    >
      <form id="revoke-agent" className="stack" onSubmit={submit}>
        <ul className="stack stack-tight text-sm">
          <li>Запросы с текущим ключом отклоняются.</li>
          <li>Сайты продолжают работать, операции с ними недоступны.</li>
          <li>
            Для возврата выпустите новый токен и переустановите агента с параметром <span className="mono">--reenroll</span>.
          </li>
        </ul>
        <Field label="Для подтверждения введите имя сервера" htmlFor="revoke-confirm" hint={node.name}>
          <Input id="revoke-confirm" value={typed} onChange={(event) => setTyped(event.target.value)} placeholder={node.name} autoComplete="off" />
        </Field>
        {revoke.isError ? (
          <p className="field-error" role="alert">
            {errorText(revoke.error)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
